import { Navigate, Route, Routes } from 'react-router-dom';
import { HashRouter } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { TenantScopeProvider } from './context/TenantScopeContext';
import LoginPage from './pages/LoginPage';
import PartnersPage from './pages/PartnersPage';
import TenantsPage from './pages/TenantsPage';
import TenantDetailPage from './pages/TenantDetailPage';
import BrokerConsolePage from './pages/BrokerConsolePage';
import VulnerabilitiesPage from './pages/VulnerabilitiesPage';
import ReportsPage from './pages/ReportsPage';
import ReportTemplatesPage from './pages/ReportTemplatesPage';
import EventsExplorerPage from './pages/EventsExplorerPage';
import CasesPage from './pages/CasesPage';
import CaseDetailPage from './pages/CaseDetailPage';
import OverviewPage from './pages/OverviewPage';
import AlertsPage from './pages/AlertsPage';
import AlertDetailPage from './pages/AlertDetailPage';
import SettingsPage from './pages/SettingsPage';
import DataQualityPage from './pages/DataQualityPage';
import LogSourcesPage from './pages/LogSourcesPage';
import ParserLabPage from './pages/ParserLabPage';
import AssetsPage from './pages/AssetsPage';
import AssetDetailPage from './pages/AssetDetailPage';
import UsersPage from './pages/UsersPage';
import DetectionRulesPage from './pages/DetectionRulesPage';
import GuidesPage from './pages/GuidesPage';
import RemoteAccessPage from './pages/RemoteAccessPage';
import MitreCoveragePage from './pages/MitreCoveragePage';
import SocOpsPage from './pages/SocOpsPage';
import SecurityPage from './pages/SecurityPage';
import OnboardingWizardPage from './pages/OnboardingWizardPage';
import OnboardingTemplatePage from './pages/OnboardingTemplatePage';
import NotificationsPage from './pages/NotificationsPage';
import ComingSoon from './components/ComingSoon';
import { canAccessPath } from './utils/access';
import Layout from './components/Layout';

// Route negata per ruolo/scope (utils/access.ts): rimanda alla Overview invece di mostrare la
// pagina — chi non vede la voce di menu non deve arrivarci nemmeno con l'URL.
function Guarded({ path, children }: { path: string; children: React.ReactNode }) {
  const { user } = useAuth();
  if (!canAccessPath(user, path)) return <Navigate to="/overview" replace />;
  return <>{children}</>;
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();
  if (isLoading) return <div className="p-8 text-slate-500">Loading…</div>;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route
        path="/*"
        element={
          <ProtectedRoute>
            <Layout>
              <Routes>
                <Route path="/" element={<Navigate to="/overview" replace />} />
                <Route path="/partners" element={<PartnersPage />} />
                <Route path="/tenants" element={<TenantsPage />} />
                <Route path="/overview" element={<OverviewPage />} />
                <Route path="/soc-ops" element={<SocOpsPage />} />
                <Route path="/incidents" element={<CasesPage />} />
                <Route path="/incidents/:caseId" element={<CaseDetailPage />} />
                <Route path="/alerts" element={<AlertsPage />} />
                <Route path="/alerts/:alertId" element={<AlertDetailPage />} />
                <Route path="/risky-assets" element={<Guarded path="/risky-assets"><ComingSoon title="Risky Assets" /></Guarded>} />
                <Route path="/logs" element={<EventsExplorerPage />} />
                <Route path="/whitelists" element={<Guarded path="/whitelists"><ComingSoon title="Whitelists" /></Guarded>} />
                <Route path="/detection-rules" element={<DetectionRulesPage />} />
                <Route path="/responses" element={<Guarded path="/responses"><ComingSoon title="SOAR" /></Guarded>} />
                <Route path="/risk-management" element={<VulnerabilitiesPage />} />
                <Route path="/reports" element={<ReportsPage />} />
                <Route path="/report-templates" element={<ReportTemplatesPage />} />
                <Route path="/asset-center" element={<AssetsPage />} />
                <Route path="/asset-center/:assetId" element={<AssetDetailPage />} />
                <Route path="/sites-brokers" element={<TenantDetailPage />} />
                <Route path="/broker-console/:brokerId" element={<BrokerConsolePage />} />
                <Route path="/settings" element={<Guarded path="/settings"><SettingsPage /></Guarded>} />
                <Route path="/data-quality" element={<DataQualityPage />} />
                <Route path="/log-sources" element={<LogSourcesPage />} />
                <Route path="/parser-lab" element={<Guarded path="/parser-lab"><ParserLabPage /></Guarded>} />
                <Route path="/users" element={<UsersPage />} />
                <Route path="/remote-access" element={<RemoteAccessPage />} />
                <Route path="/mitre-coverage" element={<MitreCoveragePage />} />
                <Route path="/guides" element={<GuidesPage />} />
                <Route path="/guides/:slug" element={<GuidesPage />} />
                <Route path="/security" element={<SecurityPage />} />
                <Route path="/onboarding" element={<OnboardingWizardPage />} />
                <Route path="/onboarding-template" element={<OnboardingTemplatePage />} />
                <Route path="/notifications" element={<NotificationsPage />} />
              </Routes>
            </Layout>
          </ProtectedRoute>
        }
      />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <TenantScopeProvider>
        <HashRouter>
          <AppRoutes />
        </HashRouter>
      </TenantScopeProvider>
    </AuthProvider>
  );
}
